// Reusable image field for admin: upload file to Supabase Storage, paste URL, or fetch & resize from URL.
// Uploads go to store-assets/{folder}/ and public URL is set on the form field.
// When the image is from our Supabase bucket, offers "Eliminar del storage" to delete the file and clear the field.

import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Download, Upload, Link as LinkIcon, Trash2 } from 'lucide-react';
import { uploadFileToSupabase, fetchResizeAndUpload, type ImageFolder } from '@/lib/image-utils';
import { isSupabaseStorageUrl } from '@/lib/store-images';
import { postWorkerApi } from '@/lib/worker-api';
import { useToast } from '@/hooks/use-toast';

export interface ImageUploadFieldProps {
  label: string;
  value: string;
  onChange: (url: string) => void;
  /** Subfolder in bucket: categories | families | items | brands */
  folder: ImageFolder;
  /** Optional: limit file size in bytes for direct upload */
  maxSizeBytes?: number;
  /** Optional: when changed, preview img reloads (cache-bust) without changing stored URL. Use after regenerating same path (e.g. AI). */
  previewKey?: number;
}

export function ImageUploadField({
  label,
  value,
  onChange,
  folder,
  maxSizeBytes: directUploadMaxSize = 5 * 1024 * 1024,
  previewKey,
}: ImageUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [fetchingUrl, setFetchingUrl] = useState(false);
  const [deletingStorage, setDeletingStorage] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [urlInput, setUrlInput] = useState('');
  const { toast } = useToast();

  // Handle direct file upload
  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setUploadError('Please choose an image file (e.g. JPG, PNG).');
      return;
    }
    if (file.size > directUploadMaxSize) {
      setUploadError(`File too large (max ${Math.round(directUploadMaxSize / 1024 / 1024)}MB).`);
      return;
    }
    setUploadError(null);
    setUploading(true);
    try {
      const publicUrl = await uploadFileToSupabase(file, folder);
      onChange(publicUrl);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  // Fetch image from URL, resize, and upload
  async function handleFetchFromUrl() {
    const url = urlInput.trim();
    if (!url) {
      setUploadError('Please enter a URL');
      return;
    }

    setUploadError(null);
    setFetchingUrl(true);

    try {
      const publicUrl = await fetchResizeAndUpload(url, folder);
      onChange(publicUrl);
      setUrlInput('');
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Fetch failed');
    } finally {
      setFetchingUrl(false);
    }
  }

  async function handleDeleteFromStorage() {
    if (!value?.trim()) return;
    if (!isSupabaseStorageUrl(value)) return;
    if (!window.confirm('¿Eliminar la imagen del storage y quitarla del producto? Esta acción no se puede deshacer.')) return;
    setDeletingStorage(true);
    try {
      const result = await postWorkerApi<{ ok?: boolean }>('/api/delete-storage-image', { url: value });
      if (!result.ok) throw new Error(result.error || `Error ${result.status}`);
      onChange('');
      toast({ title: 'Imagen eliminada', description: 'Se ha eliminado del storage y del campo.' });
    } catch (e) {
      toast({ title: 'Error', description: e instanceof Error ? e.message : 'No se pudo eliminar', variant: 'destructive' });
    } finally {
      setDeletingStorage(false);
    }
  }

  const isProcessing = uploading || fetchingUrl || deletingStorage;
  const canDeleteFromStorage = value?.trim() && isSupabaseStorageUrl(value);

  return (
    <div className="space-y-2 min-w-0">
      <Label>{label}</Label>
      <div className="flex flex-col gap-2 min-w-0">
        {/* Row 1: Upload file button */}
        <div className="flex gap-2 items-center">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
            disabled={isProcessing}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={isProcessing}
            className="gap-1.5"
          >
            <Upload className="h-3.5 w-3.5" />
            {uploading ? 'Uploading…' : 'Upload file'}
          </Button>
        </div>

        {/* Row 2: Fetch from URL */}
        <div className="flex gap-2 items-center min-w-0">
          <Input
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="Paste image URL to fetch & resize…"
            className="flex-1 min-w-0"
            disabled={isProcessing}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleFetchFromUrl}
            disabled={isProcessing || !urlInput.trim()}
            className="gap-1.5 shrink-0"
          >
            <Download className="h-3.5 w-3.5" />
            {fetchingUrl ? 'Fetching…' : 'Fetch & Upload'}
          </Button>
        </div>

        {/* Row 3: Direct URL input (for manual paste) - constrained so long URLs don't overflow modal */}
        <div className="flex gap-2 items-center min-w-0">
          <LinkIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Or paste direct URL (no resize)"
            className="flex-1 min-w-0 overflow-hidden text-ellipsis"
          />
        </div>

        {uploadError && <p className="text-sm text-destructive">{uploadError}</p>}

        {/* Preview: only image, no URL text to avoid long links breaking the modal. previewKey forces reload when same URL is replaced (e.g. AI regenerate). */}
        {value && (
          <div className="mt-1 p-2 bg-zinc-50 rounded-lg border border-zinc-200 min-w-0 max-w-full flex flex-col gap-2">
            <img
              src={previewKey != null ? `${value}${value.includes('?') ? '&' : '?'}t=${previewKey}` : value}
              alt="Preview"
              className="max-h-24 w-auto max-w-full object-contain rounded"
              onError={() => {}}
            />
            {canDeleteFromStorage && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleDeleteFromStorage}
                disabled={deletingStorage}
                className="gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10"
                title="Eliminar del storage y quitar del producto"
              >
                <Trash2 className="h-3.5 w-3.5" />
                {deletingStorage ? 'Eliminando…' : 'Eliminar del storage'}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
